import ErrorBoundary from '@components/layout/errorBoundary';
import { NavigationStyles } from '@interfaces/role';
import { BootSpinner } from '@ovComponents/1-primative/bootSpinner/bootSpinner';
import type { SubSection } from '@ovComponents/2-component/navSection/navSection';
import BulkTraderRevamp from '@ovComponents/4-module/bulkTrader/bulkTrader/bulkTrader';
import Layout from '@ovComponents/4-module/layout/layout';
import { useMenuSections } from '@ovComponents/4-module/layout/sidebar';
import { BulkTraderBreadcrumb as BulkTraderRevampBreadcrumb } from '@ovComponents/5-page/bulkTrader/bulkTraderBreadcrumb';
import BulkTradersRevamp from '@ovComponents/5-page/bulkTrader/bulkTraders';
import { BulkUpsertItemsBreadcrumb } from '@ovComponents/5-page/bulkUpsertRequests/components/bulkUpsertItemsBreadcrumb';
import { ClientReportTemplateBreadcrumb } from '@ovComponents/5-page/clientReportTemplate/clientReportTemplateBreadcrumb';
import { ConfigurableTablePageBreadcrumb } from '@ovComponents/5-page/configurableTablePage/components/configurableTablePageBreadcrumb';
import { DailyFeeDetailsBreadcrumb } from '@ovComponents/5-page/dailyFeeDetails/dailyFeeDetailsBreadcrumb';
import { DashboardBreadcrumb } from '@ovComponents/5-page/dashboard/dashboardBreadcrumb';
import { FeeReportBreadcrumb } from '@ovComponents/5-page/feeReport/feeReportBreadcrumb';
import { HomePage } from '@ovComponents/5-page/homePage/homePage';
import { ClientGroupBreadcrumb } from '@ovComponents/5-page/households/clientGroupBreadcrumb';
import { ModelPortfoliosBreadcrumb } from '@ovComponents/5-page/modelPortfolios/modelPortfoliosBreadcrumb';
import PortfolioTradingHubV2 from '@ovComponents/5-page/portfolioTradingHub/components/portfolioTradingHubV2';
import PortfolioTradingHub from '@ovComponents/5-page/portfolioTradingHub/portfolioTradingHub';
import ReconFlagsPage from '@ovComponents/5-page/reconFlags/reconFlagsPage';
import { CustomReportBreadcrumb } from '@ovComponents/5-page/reports/customReportBreadcrumb';
import { AlertBanners } from '@ovComponents/5-page/settings/alertBanners/alertBanners';
import { FlagPoliciesSettings } from '@ovComponents/5-page/settings/flagPolicies/flagPolicies';
import { TextDesignerSettings } from '@ovComponents/5-page/settings/textDesigner/textDesigner';
import { ClientReportBreadcrumb } from '@ovComponents/5-page/viewClientReport/clientReportBreadcrumb';
import { AccountGroupBreadcrumb } from '@pages/accountGroupsDetail/accountGroupBreadcrumb';
import { AccountBreadcrumb } from '@pages/accountsDetail/accountBreadcrumb';
import { PortfolioModelBreadcrumb } from '@pages/buildModels/portfolioModelBreadcrumb';
import { ImportFileBreadcrumb } from '@pages/bulkImportFileLineItems/components/importFileBreadcrumb';
import { BulkImportBreadcrumb } from '@pages/bulkImportFiles/components/bulkImportBreadcrumb';
import { ClientBreadcrumb } from '@pages/client/clientBreadcrumb';
import { EntityBreadcrumb } from '@pages/client/entityBreadcrumb';
import { GoalBreadcrumb } from '@pages/goalsDetails/goalBreadcrumb';
import { BulkTraderBreadcrumb } from '@pages/legacy/bulkTrader/bulkTraderBreadcrumb';
import { OrgDetailsBreadcrumb } from '@pages/orgDetails/orgDetailsBreadcrumb';
import { RebalanceBreadcrumb } from '@pages/rebalance/rebalanceBreadcrumb';
import { SubAccountBreadcrumb } from '@pages/subAccountsDetail/subAccountBreadcrumb';
import { TaxLossHarvestingBreadcrumb } from '@pages/taxLossHarvestingEvents/taxLossHarvestingBreadcrumb';
import {
  UserContext,
  useFeatureFlags,
  usePermissions,
} from '@providers/userContextProvider';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useContext, useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useNavigate,
} from 'react-router-dom';
import { lazyComponent } from './lazy/lazyComponent';

// Pages load on demand via React Router's `lazy:`; flag-guarded and fallback pages stay eager.

// --- @ovComponents/5-page ---
const lazyAccountGroups = lazyComponent(
  () => import('@ovComponents/5-page/accountGroups/accountGroups'),
  'AccountGroups',
);
const lazyAccounts = lazyComponent(
  () => import('@ovComponents/5-page/accounts/accounts'),
  'Accounts',
);
const lazyAccountsReview = lazyComponent(
  () => import('@ovComponents/5-page/accountsReview/accountsReview'),
  'AccountsReview',
);
const lazyAllNewsAndInsights = lazyComponent(
  () => import('@ovComponents/5-page/allNewsAndInsights/allNewsAndInsights'),
  'AllNewsAndInsights',
);
const lazyAllTransactions = lazyComponent(
  () => import('@ovComponents/5-page/allTransactions/allTransactions'),
  'AllTransactions',
);
const lazyAnnualSuitabilityReview = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/annualSuitabilityReview/annualSuitabilityReview'
    ),
  'AnnualSuitabilityReview',
);
const lazyAssetClasses = lazyComponent(
  () => import('@ovComponents/5-page/assetClasses/assetClasses'),
  'AssetClasses',
);
const lazyBankAccountsToReview = lazyComponent(
  () =>
    import('@ovComponents/5-page/bankAccountsToReview/bankAccountsToReview'),
  'BankAccountsToReview',
);
const lazyBillingManagement = lazyComponent(
  () => import('@ovComponents/5-page/billingManagement/billingManagement'),
  'default',
);
const lazyBulkUpsertItems = lazyComponent(
  () => import('@ovComponents/5-page/bulkUpsertRequests/bulkUpsertItems'),
  'BulkUpsertItems',
);
const lazyBulkUpsertRequests = lazyComponent(
  () => import('@ovComponents/5-page/bulkUpsertRequests/bulkUpsertRequests'),
  'BulkUpsertRequests',
);
const lazyClientReportTemplate = lazyComponent(
  () =>
    import('@ovComponents/5-page/clientReportTemplate/clientReportTemplate'),
  'ClientReportTemplate',
);
const lazyComplianceReview = lazyComponent(
  () => import('@ovComponents/5-page/complianceReview/complianceReview'),
  'ComplianceReview',
);
const lazyConfigurableTablePage = lazyComponent(
  () =>
    import('@ovComponents/5-page/configurableTablePage/configurableTablePage'),
  'ConfigurableTablePage',
);
const lazyConfigureWorkflowPage = lazyComponent(
  () =>
    import('@ovComponents/5-page/configureWorkflowPage/configureWorkflowPage'),
  'ConfigureWorkflowPage',
);
const lazyDailyFeeDetails = lazyComponent(
  () => import('@ovComponents/5-page/dailyFeeDetails/dailyFeeDetails'),
  'DailyFeeDetails',
);
const lazyDashboard = lazyComponent(
  () => import('@ovComponents/5-page/dashboard/dashboard'),
  'Dashboard',
);
const lazyEditFormTemplate = lazyComponent(
  () => import('@ovComponents/5-page/documentManagement/editFormTemplate'),
  'default',
);
const lazyEditDashboard = lazyComponent(
  () => import('@ovComponents/5-page/editDashboard/editDashboard'),
  'EditDashboard',
);
const lazyElderlyClients = lazyComponent(
  () => import('@ovComponents/5-page/elderlyClients/elderlyClients'),
  'ElderlyClients',
);
const lazyFeeReport = lazyComponent(
  () => import('@ovComponents/5-page/feeReport/feeReport'),
  'FeeReport',
);
const lazyHouseholds = lazyComponent(
  () => import('@ovComponents/5-page/households/households'),
  'Households',
);
const lazyIndividuals = lazyComponent(
  () => import('@ovComponents/5-page/individuals/individuals'),
  'Individuals',
);
const lazyInstitutionsToReview = lazyComponent(
  () =>
    import('@ovComponents/5-page/institutionsToReview/institutionsToReview'),
  'InstitutionsToReview',
);
const lazyManageNewsAndInsight = lazyComponent(
  () =>
    import('@ovComponents/5-page/manageNewsAndInsights/manageNewsAndInsights'),
  'ManageNewsAndInsight',
);
const lazyModelFamilies = lazyComponent(
  () => import('@ovComponents/5-page/modelFamilies/modelFamilies'),
  'ModelFamilies',
);
const lazyModelPortfolioDetails = lazyComponent(
  () => import('@ovComponents/5-page/modelPortfolios/modelPortfolioDetails'),
  'default',
);
const lazyModelPortfolios = lazyComponent(
  () => import('@ovComponents/5-page/modelPortfolios/modelPortfolios'),
  'ModelPortfolios',
);
const lazyNewReport = lazyComponent(
  () => import('@ovComponents/5-page/newReport/newReport'),
  'NewReport',
);
const lazyNonIndividuals = lazyComponent(
  () => import('@ovComponents/5-page/nonIndividuals/nonIndividuals'),
  'NonIndividuals',
);
const lazyPageConfiguration = lazyComponent(
  () => import('@ovComponents/5-page/pageConfiguration/pageConfiguration'),
  'PageConfiguration',
);
const lazyPortfolioTemplates = lazyComponent(
  () => import('@ovComponents/5-page/portfolioTemplates/portfolioTemplates'),
  'PortfolioTemplates',
);
const lazyProductShelfPage = lazyComponent(
  () => import('@ovComponents/5-page/productShelf/productShelfPage'),
  'ProductShelfPage',
);
const lazyRebalanceReport = lazyComponent(
  () => import('@ovComponents/5-page/rebalanceReport/rebalanceReport'),
  'RebalanceReport',
);
const lazyReconciliation = lazyComponent(
  () => import('@ovComponents/5-page/reconciliation/reconciliation'),
  'default',
);
const lazyAccountSnapshot = lazyComponent(
  () => import('@ovComponents/5-page/reconciliationV2/accountSnapshot'),
  'AccountSnapshot',
);
const lazyReconciliationV2 = lazyComponent(
  () => import('@ovComponents/5-page/reconciliationV2/reconciliationV2'),
  'ReconciliationV2',
);
const lazyReports = lazyComponent(
  () => import('@ovComponents/5-page/reports/reports'),
  'Reports',
);
const lazyStatementsReview = lazyComponent(
  () => import('@ovComponents/5-page/statementsReview/statementsReview'),
  'StatementsReview',
);
const lazyStaticPageWidgetConfiguration = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/staticPageWidgetConfiguration/staticPageWidgetConfiguration'
    ),
  'StaticPageWidgetConfiguration',
);
const lazyTransactionMonitoring = lazyComponent(
  () =>
    import('@ovComponents/5-page/transactionMonitoring/transactionMonitoring'),
  'TransactionMonitoring',
);
const lazyViewClientReport = lazyComponent(
  () => import('@ovComponents/5-page/viewClientReport/viewClientReport'),
  'ViewClientReport',
);
const lazyViewPage = lazyComponent(
  () => import('@ovComponents/5-page/viewPage/viewPage'),
  'ViewPage',
);
const lazyViewReport = lazyComponent(
  () => import('@ovComponents/5-page/viewReport/viewReport'),
  'ViewReport',
);
const lazyWorkflows = lazyComponent(
  () => import('@ovComponents/5-page/workflows/workflows'),
  'Workflows',
);

// --- @ovComponents/5-page/settings ---
const lazyAccessSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/accessSettings/accessSettings'),
  'AccessSettings',
);
const lazyAccessUsersSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/accessUsers/accessUsers'),
  'AccessUsersSettings',
);
const lazyAccountSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/accounts/accountSettings'),
  'AccountSettings',
);
const lazyAccountsTypesSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/accounts/accounts'),
  'AccountsTypesSettings',
);
const lazyEditAlertBanner = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/alertBanners/components/editAlertBanner'
    ),
  'EditAlertBanner',
);
const lazyNewAlertBanner = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/alertBanners/components/newAlertBanner'
    ),
  'NewAlertBanner',
);
const lazyApiTokensSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/apiTokens/apiTokens'),
  'ApiTokensSettings',
);
const lazyAuthenticationSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/authentication/authentication'),
  'AuthenticationSettings',
);
const lazyAuthenticationDesignerSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/authenticationDesigner/authenticationDesigner'
    ),
  'AuthenticationDesignerSettings',
);
const lazyBillingSchedulesSettings = lazyComponent(
  () =>
    import('@ovComponents/5-page/settings/billingSchedules/billingSchedules'),
  'BillingSchedulesSettings',
);
const lazyBulkImportsSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/bulkImport/bulkImport'),
  'BulkImportsSettings',
);
const lazyBulkImportFileLineItemsSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/bulkImportFileLineItems/bulkImportFileLineItems'
    ),
  'BulkImportFileLineItemsSettings',
);
const lazyBulkImportFilesSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/bulkImportFiles/bulkImportFiles'),
  'BulkImportFilesSettings',
);
const lazyClientReports = lazyComponent(
  () => import('@ovComponents/5-page/settings/clientReports/clientReports'),
  'ClientReports',
);
const lazyComplianceSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/compliance/compliance'),
  'ComplianceSettings',
);
const lazyConfigurableTablePageEditor = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/configurableTablePages/components/configurableTablePageEditor'
    ),
  'ConfigurableTablePageEditor',
);
const lazyConfigurableTablePagesSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/configurableTablePages/configurableTablePages'
    ),
  'ConfigurableTablePagesSettings',
);
const lazyCustodianConnectionDetail = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/custodianConnections/components/detail/custodianConnectionDetail'
    ),
  'CustodianConnectionDetail',
);
const lazyCustodianConnectionsSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/custodianConnections/custodianConnections'
    ),
  'CustodianConnectionsSettings',
);
const lazyCustomAttributeLabelsSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/customAttributeLabels/customAttributeLabels'
    ),
  'CustomAttributeLabelsSettings',
);
const lazyCustomFieldsSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/customFields/customFields'),
  'CustomFieldsSettings',
);
const lazyDashboards = lazyComponent(
  () => import('@ovComponents/5-page/settings/dashboards/dashboards'),
  'Dashboards',
);
const lazyDesignerSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/designer/designer'),
  'DesignerSettings',
);
const lazyDowntimeSchedulesSettings = lazyComponent(
  () =>
    import('@ovComponents/5-page/settings/downtimeSchedule/downtimeSchedule'),
  'DowntimeSchedulesSettings',
);
const lazyEmbeddedSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/embedded/embedded'),
  'EmbeddedSettings',
);
const lazyErrorPageDesignerSettings = lazyComponent(
  () =>
    import('@ovComponents/5-page/settings/errorPageDesigner/errorPageDesigner'),
  'ErrorPageDesignerSettings',
);
const lazyFeatureFlagsSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/featureFlags/featureFlags'),
  'FeatureFlagsSettings',
);
const lazyFeeGridsSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/feeGrids/feeGrids'),
  'FeeGridsSettings',
);
const lazyFeeSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/feeSettings/feeSettings'),
  'FeeSettings',
);
const lazyFormDefinitionsSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/formDefinitions/formDefinitions'),
  'FormDefinitionsSettings',
);
const lazyForms = lazyComponent(
  () => import('@ovComponents/5-page/settings/forms/forms'),
  'Forms',
);
const lazyGeneralSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/general/general'),
  'GeneralSettings',
);
const lazyGeneralProductExperienceSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/generalProductExperience/generalProductExperience'
    ),
  'GeneralProductExperienceSettings',
);
const lazyHomePageConfigurationEditor = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/homePageConfigurations/components/homePageConfigurationEditor'
    ),
  'HomePageConfigurationEditor',
);
const lazyHomePageConfigurationsSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/homePageConfigurations/homePageConfigurations'
    ),
  'HomePageConfigurationsSettings',
);
const lazyIntegrationSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/integrations/integrations'),
  'IntegrationSettings',
);
const lazyLocalizationSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/localization/localization'),
  'LocalizationSettings',
);
const lazyLocalizationConfigurationSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/localizationConfiguration/localizationConfiguration'
    ),
  'LocalizationConfigurationSettings',
);
const lazyNewsAndInsights = lazyComponent(
  () => import('@ovComponents/5-page/settings/newsAndInsights/newsAndInsights'),
  'NewsAndInsights',
);
const lazyNotifications = lazyComponent(
  () => import('@ovComponents/5-page/settings/notifications/notifications'),
  'Notifications',
);
const lazyPageConfigurationsSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/pageConfigurations/pageConfigurations'
    ),
  'PageConfigurationsSettings',
);
const lazyPortfolioManagementSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/portfolioManagementSettings/portfolioManagementSettings'
    ),
  'PortfolioManagementSettings',
);
const lazyRepCodesSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/repCodes/repCodes'),
  'RepCodesSettings',
);
const lazyRoleProfilesSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/roleProfiles/roleProfiles'),
  'RoleProfilesSettings',
);
const lazyRoleSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/roleSettings/roleSettings'),
  'RoleSettings',
);
const lazyRolesSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/roles/roles'),
  'RolesSettings',
);
const lazyStatementsAndTaxDocumentsSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/statementsAndTaxDocuments/statementsAndTaxDocuments'
    ),
  'StatementsAndTaxDocumentsSettings',
);
const lazyStaticPageWidgetConfigurationsSettings = lazyComponent(
  () =>
    import(
      '@ovComponents/5-page/settings/staticPageWidgetConfigurations/staticPageWidgetConfigurations'
    ),
  'StaticPageWidgetConfigurationsSettings',
);
const lazyTaxRankingsSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/taxRankings/taxRankings'),
  'TaxRankingsSettings',
);
const lazyTradesSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/trades/trades'),
  'TradesSettings',
);
const lazyTradingSchedulesSettings = lazyComponent(
  () =>
    import('@ovComponents/5-page/settings/tradingSchedules/tradingSchedules'),
  'TradingSchedulesSettings',
);
const lazyTransfersSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/transfers/transfers'),
  'TransfersSettings',
);
const lazyWebhooksSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/webhooks/webhooks'),
  'WebhooksSettings',
);
const lazyWorkflowsSettings = lazyComponent(
  () => import('@ovComponents/5-page/settings/workflows/workflows'),
  'WorkflowsSettings',
);

// --- @pages ---
const lazyAccountGroupsDetail = lazyComponent(
  () => import('@pages/accountGroupsDetail'),
  'default',
);
const lazyAccountsDetail = lazyComponent(
  () => import('@pages/accountsDetail'),
  'default',
);
const lazyBillingSchedules = lazyComponent(
  () => import('@pages/billingSchedules'),
  'default',
);
const lazyBuildModels = lazyComponent(
  () => import('@pages/buildModels'),
  'default',
);
const lazyBulkImportFileLineItems = lazyComponent(
  () => import('@pages/bulkImportFileLineItems'),
  'default',
);
const lazyBulkImport = lazyComponent(
  () => import('@pages/bulkImportFiles'),
  'default',
);
const lazyBulkImports = lazyComponent(
  () => import('@pages/bulkImports'),
  'default',
);
const lazyClientV2 = lazyComponent(() => import('@pages/client'), 'default');
const lazyDevSettings = lazyComponent(
  () => import('@pages/devSettings'),
  'default',
);
const lazyFeeGrids = lazyComponent(() => import('@pages/feeGrids'), 'default');
const lazyGoalsDetail = lazyComponent(
  () => import('@pages/goalsDetails'),
  'default',
);
const lazyHouseHold = lazyComponent(
  () => import('@pages/household'),
  'default',
);
const lazyBulkTrader = lazyComponent(
  () => import('@pages/legacy/bulkTrader'),
  'default',
);
const lazyBulkTraders = lazyComponent(
  () => import('@pages/legacy/bulkTraders'),
  'default',
);
const lazyOrgDetails = lazyComponent(
  () => import('@pages/orgDetails'),
  'default',
);
const lazyOrgSettings = lazyComponent(
  () => import('@pages/orgSettings'),
  'default',
);
const lazyPortfolioOptimizer = lazyComponent(
  () => import('@pages/portfolioOptimizer'),
  'default',
);
const lazyPortfolioOptimizers = lazyComponent(
  () => import('@pages/portfolioOptimizers'),
  'default',
);
const lazyRebalance = lazyComponent(
  () => import('@pages/rebalance'),
  'default',
);
const lazyRebalanceReportV1 = lazyComponent(
  () => import('@pages/rebalanceReportV1'),
  'default',
);
const lazyRebalances = lazyComponent(
  () => import('@pages/rebalances'),
  'default',
);
const lazyRefinitivShowcase = lazyComponent(
  () => import('@pages/refinitivShowcase'),
  'RefinitivShowcase',
);
const lazySchedules = lazyComponent(
  () => import('@pages/schedules'),
  'default',
);
const lazySubAccountsDetail = lazyComponent(
  () => import('@pages/subAccountsDetail'),
  'default',
);
const lazyTaxLossHarvesting = lazyComponent(
  () => import('@pages/taxLossHarvesting'),
  'default',
);
const lazyTaxLossHarvestingEvents = lazyComponent(
  () => import('@pages/taxLossHarvestingEvents'),
  'default',
);
const lazyFormMapper = lazyComponent(
  () => import('@pages/formMapper'),
  'FormMapper',
);

const FlagsmithRouteGuard = ({
  flag,
  redirectTo,
  children,
}: {
  flag: FlagsmithFlags;
  redirectTo: string;
  children: React.ReactNode;
}) => {
  const { flagEnabled } = useFeatureFlags();
  if (!flagEnabled(flag)) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
};

const newsAndInsightsRoutes = (
  <Route>
    <Route
      path='newsAndInsights/:widgetId'
      lazy={lazyAllNewsAndInsights}
      handle={{ breadcrumb: 'navMenu:menu.newsAndInsights' }}
    />
  </Route>
);

const clientReportRoutes = (
  <Route>
    <Route
      path='clientReport/:clientReportTemplateId/:objectId'
      lazy={lazyViewClientReport}
      handle={{ breadcrumb: ClientReportBreadcrumb }}
    />
  </Route>
);

const subAccountRoutes = (
  <Route
    path='subaccount/:subAccountId'
    handle={{ breadcrumb: SubAccountBreadcrumb }}
  >
    <Route index lazy={lazySubAccountsDetail} />
    {clientReportRoutes}
  </Route>
);

const accountRoutes = (
  <Route path='account/:accountId' handle={{ breadcrumb: AccountBreadcrumb }}>
    <Route index lazy={lazyAccountsDetail} />
    {clientReportRoutes}
    {subAccountRoutes}
  </Route>
);

const accountGroupRoutes = (
  <Route
    path='accountGroup/:accountGroupId'
    handle={{ breadcrumb: AccountGroupBreadcrumb }}
  >
    <Route index lazy={lazyAccountGroupsDetail} />
    {clientReportRoutes}
    {accountRoutes}
  </Route>
);

const goalAccountAccountGroupSubaccountRoutes = (
  <Route>
    <Route path='goal/:goalId' handle={{ breadcrumb: GoalBreadcrumb }}>
      <Route index lazy={lazyGoalsDetail} />
      {clientReportRoutes}
      {subAccountRoutes}
    </Route>

    {accountRoutes}

    {accountGroupRoutes}
  </Route>
);

const householdRoutes = (
  <Route>
    <Route
      path='household/:clientGroupId'
      handle={{ breadcrumb: ClientGroupBreadcrumb }}
    >
      <Route index lazy={lazyHouseHold} />
      {clientReportRoutes}
      {goalAccountAccountGroupSubaccountRoutes}
      {newsAndInsightsRoutes}
    </Route>
  </Route>
);

const accesibleRoutes = (
  accessiblePages: string[],
  isHouseholdDefaultView?: boolean,
) => (
  <Route path='/' element={<Layout />} errorElement={<ErrorBoundary />}>
    <Route index element={<FallbackRoute />} />

    {accessiblePages.includes('HOME') && (
      <Route
        path='dashboard/:dashboardId'
        lazy={lazyDashboard}
        handle={{ breadcrumb: DashboardBreadcrumb }}
      />
    )}
    {accessiblePages.includes('PRODUCT_SHELF') && (
      <Route
        path='productShelf'
        lazy={lazyProductShelfPage}
        handle={{ breadcrumb: 'navMenu:menu.productShelfPage' }}
      />
    )}
    {accessiblePages.includes('ASSET_CLASSES') && (
      <Route
        path='assetClasses'
        lazy={lazyAssetClasses}
        handle={{ breadcrumb: 'navMenu:menu.assetClassesPage' }}
      />
    )}
    {accessiblePages.includes('SCHEDULES') && (
      <Route
        path='schedules'
        lazy={lazySchedules}
        handle={{ breadcrumb: 'navMenu:menu.tradingSchedules' }}
      />
    )}
    {(accessiblePages.includes('HOUSEHOLDS') || isHouseholdDefaultView) && (
      <Route
        path='households'
        handle={{ breadcrumb: 'navMenu:menu.households' }}
      >
        {accessiblePages.includes('HOUSEHOLDS') && (
          <Route index lazy={lazyHouseholds} />
        )}
        <Route
          path=':clientGroupId'
          handle={{ breadcrumb: ClientGroupBreadcrumb }}
        >
          <Route index lazy={lazyHouseHold} />
          {clientReportRoutes}
          {accountGroupRoutes}
          {(accessiblePages.includes('INDIVIDUAL_CLIENTS') ||
            accessiblePages.includes('NON_INDIVIDUAL_CLIENTS')) && (
            <Route>
              <Route
                path=':clients/:userId'
                handle={{ breadcrumb: ClientBreadcrumb }}
              >
                <Route index lazy={lazyClientV2} />
                {clientReportRoutes}
                {goalAccountAccountGroupSubaccountRoutes}
                {newsAndInsightsRoutes}
              </Route>
              {goalAccountAccountGroupSubaccountRoutes}
              {newsAndInsightsRoutes}
            </Route>
          )}
        </Route>
      </Route>
    )}
    {accessiblePages.includes('ACCOUNTS') && (
      <Route
        path='accounts'
        handle={{ breadcrumb: 'navMenu:menu.accountsList' }}
      >
        <Route index lazy={lazyAccounts} />
      </Route>
    )}
    {accessiblePages.includes('ACCOUNTS') &&
      !accessiblePages.includes('INDIVIDUAL_CLIENTS') && (
        <Route
          path='clients/:userId/account/:accountId'
          handle={{ breadcrumb: AccountBreadcrumb }}
        >
          <Route index lazy={lazyAccountsDetail} />
          {clientReportRoutes}
        </Route>
      )}
    {accessiblePages.includes('ACCOUNT_GROUPS') && (
      <Route
        path='accountGroups'
        handle={{ breadcrumb: 'navMenu:menu.accountGroupsList' }}
      >
        <Route index lazy={lazyAccountGroups} />
        <Route
          path=':accountGroupId'
          handle={{ breadcrumb: AccountGroupBreadcrumb }}
        >
          <Route index lazy={lazyAccountGroupsDetail} />
          {clientReportRoutes}
          {accountRoutes}
        </Route>
      </Route>
    )}
    {accessiblePages.includes('INDIVIDUAL_CLIENTS') && (
      <Route>
        <Route
          path='clients'
          handle={{ breadcrumb: 'navMenu:menu.individuals' }}
        >
          <Route index lazy={lazyIndividuals} />
          <Route path=':userId' handle={{ breadcrumb: ClientBreadcrumb }}>
            <Route index lazy={lazyClientV2} />
            {clientReportRoutes}
            {goalAccountAccountGroupSubaccountRoutes}
            {householdRoutes}
            {newsAndInsightsRoutes}
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.includes('NON_INDIVIDUAL_CLIENTS') && (
      <Route>
        <Route
          path='nonIndividualClients'
          handle={{ breadcrumb: 'navMenu:menu.nonIndividualClients' }}
        >
          <Route index lazy={lazyNonIndividuals} />
          <Route path=':userId' handle={{ breadcrumb: EntityBreadcrumb }}>
            <Route index lazy={lazyClientV2} />
            {clientReportRoutes}
            {goalAccountAccountGroupSubaccountRoutes}
            {newsAndInsightsRoutes}
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.includes('BUILD_MODELS') && (
      <Route>
        <Route
          path='buildModels'
          handle={{ breadcrumb: 'navMenu:menu.modelPortfolios' }}
        >
          <Route index lazy={lazyBuildModels} />
          <Route
            path=':id'
            lazy={lazyBuildModels}
            handle={{ breadcrumb: PortfolioModelBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.includes('PORTFOLIO_MODEL_FAMILIES') && (
      <Route>
        <Route
          path='modelFamilies'
          lazy={lazyModelFamilies}
          handle={{ breadcrumb: 'navMenu:menu.modelFamily' }}
        />
      </Route>
    )}
    {accessiblePages.includes('MODEL_PORTFOLIOS') && (
      <Route>
        <Route
          path='modelPortfolios'
          handle={{ breadcrumb: ModelPortfoliosBreadcrumb }}
        >
          <Route index lazy={lazyModelPortfolios} />
          <Route
            path=':id'
            lazy={lazyModelPortfolioDetails}
            handle={{ breadcrumb: PortfolioModelBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.includes('PORTFOLIO_TEMPLATES') && (
      <Route>
        <Route
          path='portfolioTemplates'
          lazy={lazyPortfolioTemplates}
          handle={{ breadcrumb: 'navMenu:menu.portfolioTemplates' }}
        />
      </Route>
    )}
    {accessiblePages.includes('REBALANCING') && (
      <Route>
        <Route
          path='rebalanceReportv1'
          lazy={lazyRebalanceReportV1}
          handle={{ breadcrumb: 'navMenu:menu.rebalanceReportV1' }}
        />
        <Route
          path='rebalances'
          handle={{ breadcrumb: 'navMenu:menu.rebalancing' }}
        >
          <Route index lazy={lazyRebalances} />
          <Route
            path=':id'
            lazy={lazyRebalance}
            handle={{ breadcrumb: RebalanceBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.includes('REBALANCE_REPORT') && (
      <Route
        path='rebalanceReport'
        lazy={lazyRebalanceReport}
        handle={{ breadcrumb: 'navMenu:menu.rebalanceReport' }}
      />
    )}
    <Route
      path='portfolio-trading-prototype'
      element={
        <FlagsmithRouteGuard
          flag={FlagsmithFlags.portfolio_trading_prototype}
          redirectTo='/'
        >
          <PortfolioTradingHub />
        </FlagsmithRouteGuard>
      }
      handle={{ breadcrumb: 'Portfolio Trading Hub' }}
    />
    <Route
      path='portfolio-trading-prototype-v2'
      element={
        <FlagsmithRouteGuard
          flag={FlagsmithFlags.portfolio_trading_prototype}
          redirectTo='/'
        >
          <PortfolioTradingHubV2 />
        </FlagsmithRouteGuard>
      }
      handle={{ breadcrumb: 'Portfolio Trading Hub v2' }}
    />
    {accessiblePages.includes('PORTFOLIO_OPTIMIZER') && (
      <Route
        path='portfolioOptimizers'
        handle={{ breadcrumb: 'navMenu:menu.portfolioOptimizer' }}
      >
        <Route index lazy={lazyPortfolioOptimizers} />
        <Route path=':id' lazy={lazyPortfolioOptimizer} />
        {/* todo breadcrumb, check once the UI is done ? */}
      </Route>
    )}
    {accessiblePages.includes('BULK_TRADER') && (
      <Route>
        <Route
          path='bulkTrader'
          handle={{ breadcrumb: 'navMenu:menu.bulkTrader' }}
        >
          <Route index lazy={lazyBulkTraders} />
          <Route
            path=':id'
            lazy={lazyBulkTrader}
            handle={{ breadcrumb: BulkTraderBreadcrumb }}
          />
        </Route>
        <Route
          path='bulkTraderRevamp'
          element={
            <FlagsmithRouteGuard
              flag={FlagsmithFlags.bulk_trader_revamp}
              redirectTo='/'
            >
              <Outlet />
            </FlagsmithRouteGuard>
          }
          handle={{ breadcrumb: 'navMenu:menu.bulkTraderRevamp' }}
        >
          <Route index element={<BulkTradersRevamp />} />
          <Route
            path=':id'
            element={<BulkTraderRevamp />}
            handle={{ breadcrumb: BulkTraderRevampBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.includes('BULK_IMPORT') && (
      <Route>
        <Route
          path='bulkImport'
          handle={{ breadcrumb: 'navMenu:menu.bulkImport' }}
        >
          <Route index lazy={lazyBulkImports} />
          <Route
            path=':bulkImportId'
            handle={{ breadcrumb: BulkImportBreadcrumb }}
          >
            <Route index lazy={lazyBulkImport} />
            <Route
              path='file/:importFileId'
              lazy={lazyBulkImportFileLineItems}
              handle={{ breadcrumb: ImportFileBreadcrumb }}
            />
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.includes('TAX_LOSS_HARVESTING') && (
      <Route>
        <Route
          path='taxLossHarvesting'
          handle={{ breadcrumb: 'navMenu:menu.taxLossHarvesting' }}
        >
          <Route index lazy={lazyTaxLossHarvesting} />
          <Route
            path=':id'
            lazy={lazyTaxLossHarvestingEvents}
            handle={{ breadcrumb: TaxLossHarvestingBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.includes('ELDERLY_CLIENTS') && (
      <Route
        path='elderlyClients'
        lazy={lazyElderlyClients}
        handle={{ breadcrumb: 'navMenu:menu.elderlyClients' }}
      />
    )}
    {accessiblePages.includes('SUSPICIOUS_TRANSACTIONS') && (
      <Route
        path='transactionMonitoringTool'
        lazy={lazyTransactionMonitoring}
        handle={{ breadcrumb: 'navMenu:menu.transactionMonitoringTool' }}
      />
    )}
    {accessiblePages.includes('BANK_ACCOUNTS_TO_REVIEW') && (
      <Route
        path='bankAccountsToReview'
        lazy={lazyBankAccountsToReview}
        handle={{ breadcrumb: 'navMenu:menu.bankAccountsToReview' }}
      />
    )}
    {accessiblePages.includes('INSTITUTIONS_TO_REVIEW') && (
      <Route
        path='institutionsToReview'
        lazy={lazyInstitutionsToReview}
        handle={{ breadcrumb: 'navMenu:menu.institutionsToReview' }}
      />
    )}
    {accessiblePages.includes('ANNUAL_SUITABILITY_REVIEW') && (
      <Route
        path='annualSuitabilityReview'
        lazy={lazyAnnualSuitabilityReview}
        handle={{ breadcrumb: 'navMenu:menu.annualSuitabilityReview' }}
      />
    )}
    {accessiblePages.includes('COMPLIANCE_REVIEW') && (
      <Route
        path='complianceReview'
        lazy={lazyComplianceReview}
        handle={{ breadcrumb: 'navMenu:menu.complianceReview' }}
      />
    )}
    {accessiblePages.includes('RECONCILIATION') && (
      <Route
        path='reconciliation'
        lazy={lazyReconciliation}
        handle={{ breadcrumb: 'navMenu:menu.reconciliation' }}
      />
    )}
    {accessiblePages.includes('RECONCILIATION') && (
      <Route
        path='reconciliationV2'
        lazy={lazyReconciliationV2}
        handle={{ breadcrumb: 'navMenu:menu.reconciliation' }}
      />
    )}
    {accessiblePages.includes('RECONCILIATION') && (
      <Route
        path='reconciliationV2/:id/'
        lazy={lazyAccountSnapshot}
        handle={{ breadcrumb: 'navMenu:menu.reconciliation' }}
      />
    )}
    {accessiblePages.includes('RECONCILIATION') && (
      <Route
        path='reconciliation/flags'
        element={
          <FlagsmithRouteGuard
            flag={FlagsmithFlags.recon_flags_dashboard}
            redirectTo='/reconciliation'
          >
            <ReconFlagsPage />
          </FlagsmithRouteGuard>
        }
        handle={{ breadcrumb: 'navMenu:menu.flags' }}
      />
    )}
    {accessiblePages.includes('RECONCILIATION') && (
      <Route
        path='reconFlags'
        element={<Navigate to='/reconciliation/flags' replace />}
      />
    )}
    {accessiblePages.includes('STATEMENTS_REVIEW') && (
      <Route
        path='statementsReview'
        lazy={lazyStatementsReview}
        handle={{ breadcrumb: 'navMenu:menu.statementsReview' }}
      />
    )}
    {accessiblePages.includes('TRANSACTIONS') && (
      <Route
        path='transactions'
        lazy={lazyAllTransactions}
        handle={{ breadcrumb: 'navMenu:menu.transactions' }}
      />
    )}
    {accessiblePages.includes('ACCOUNTS_REVIEW') && (
      <Route
        path='accountsReview'
        lazy={lazyAccountsReview}
        handle={{ breadcrumb: 'navMenu:menu.accountsReview' }}
      />
    )}
    {accessiblePages.includes('FEE_GRIDS') && (
      <Route
        path='feeGrids'
        lazy={lazyFeeGrids}
        handle={{ breadcrumb: 'navMenu:menu.feeGrids' }}
      />
    )}
    {accessiblePages.includes('BILLING_SCHEDULES') && (
      <Route
        path='billingSchedules'
        lazy={lazyBillingSchedules}
        handle={{ breadcrumb: 'navMenu:menu.billingSchedules' }}
      />
    )}
    {accessiblePages.includes('BILLING_MANAGEMENT') && (
      <Route>
        <Route
          path='billingManagement'
          handle={{ breadcrumb: 'navMenu:menu.incurredFees' }}
        >
          <Route index lazy={lazyBillingManagement} />
          <Route
            path=':billingCycleId'
            handle={{ breadcrumb: FeeReportBreadcrumb }}
          >
            <Route index lazy={lazyFeeReport} />
            <Route
              path='subAccount/:subAccountId'
              lazy={lazyDailyFeeDetails}
              handle={{ breadcrumb: DailyFeeDetailsBreadcrumb }}
            />
          </Route>
        </Route>
      </Route>
    )}
    {accessiblePages.includes('ORGANIZATION_SETTINGS') && (
      <Route
        path='orgSettings'
        handle={{ breadcrumb: 'navMenu:menu.orgSettings' }}
      >
        <Route index lazy={lazyOrgSettings} />
        <Route
          path='configureWorkflow/:id'
          handle={{ breadcrumb: 'navMenu:menu.configureWorkflow' }}
          lazy={lazyConfigureWorkflowPage}
        />
        <Route
          path='pageConfiguration/:id'
          handle={{ breadcrumb: 'navMenu:menu.pageConfiguration' }}
          lazy={lazyPageConfiguration}
        />
        <Route
          path='staticPageWidgetConfiguration/:id'
          handle={{ breadcrumb: 'navMenu:menu.staticPageWidgetConfiguration' }}
          lazy={lazyStaticPageWidgetConfiguration}
        />
        <Route
          path='clientReportTemplate/:id'
          handle={{ breadcrumb: ClientReportTemplateBreadcrumb }}
          lazy={lazyClientReportTemplate}
        />
        <Route
          path='orgDetails/:id'
          handle={{ breadcrumb: OrgDetailsBreadcrumb }}
          lazy={lazyOrgDetails}
        />
        <Route
          path='editDashboard/:dashboardId'
          handle={{ breadcrumb: DashboardBreadcrumb }}
          lazy={lazyEditDashboard}
        />
        <Route
          path='formTemplate/:id'
          handle={{ breadcrumb: 'navMenu:menu.formTemplateEditor' }}
          lazy={lazyEditFormTemplate}
        />
      </Route>
    )}
    {accessiblePages.includes('NEWS_AND_INSIGHT') && (
      <Route
        path='newsAndInsights'
        handle={{ breadcrumb: 'navMenu:menu.newsAndInsights' }}
      >
        <Route index lazy={lazyManageNewsAndInsight} />
      </Route>
    )}
    <Route>
      <Route
        path='configurableTablePage/:configurableTablePageId'
        handle={{ breadcrumb: ConfigurableTablePageBreadcrumb }}
      >
        <Route index lazy={lazyConfigurableTablePage} />
        <Route
          path='accounts/:accountId'
          lazy={lazyAccountsDetail}
          handle={{ breadcrumb: AccountBreadcrumb }}
        />
        <Route
          path='accountGroups/:accountGroupId'
          lazy={lazyAccountGroupsDetail}
          handle={{ breadcrumb: AccountGroupBreadcrumb }}
        />
        <Route
          path='goals/:goalId'
          lazy={lazyGoalsDetail}
          handle={{ breadcrumb: GoalBreadcrumb }}
        />
        <Route
          path='households/:clientGroupId'
          handle={{ breadcrumb: ClientGroupBreadcrumb }}
        >
          <Route index lazy={lazyHouseHold} />
          {accountGroupRoutes}
          <Route
            path='account/:accountId'
            lazy={lazyAccountsDetail}
            handle={{ breadcrumb: AccountBreadcrumb }}
          />
          <Route
            path='client/:userId'
            handle={{ breadcrumb: ClientBreadcrumb }}
          >
            <Route index lazy={lazyClientV2} />
            <Route
              path='account/:accountId'
              lazy={lazyAccountsDetail}
              handle={{ breadcrumb: AccountBreadcrumb }}
            />
          </Route>
        </Route>
        <Route path='clients/:userId' handle={{ breadcrumb: ClientBreadcrumb }}>
          <Route index lazy={lazyClientV2} />
          <Route
            path='account/:accountId'
            lazy={lazyAccountsDetail}
            handle={{ breadcrumb: AccountBreadcrumb }}
          />
        </Route>
        <Route
          path='nonIndividualClients/:userId'
          handle={{ breadcrumb: EntityBreadcrumb }}
        >
          <Route index lazy={lazyNonIndividuals} />
          <Route
            path='account/:accountId'
            lazy={lazyAccountsDetail}
            handle={{ breadcrumb: AccountBreadcrumb }}
          />
        </Route>
        <Route
          path='subAccounts/:subAccountId'
          lazy={lazySubAccountsDetail}
          handle={{ breadcrumb: SubAccountBreadcrumb }}
        />
      </Route>
    </Route>
    {/* TODO: Temp path for testing */}
    <Route path='viewPage/:id/:objectId' lazy={lazyViewPage} />
    <Route
      path='viewClientReport/:clientReportTemplateId/:objectId'
      lazy={lazyViewClientReport}
    />

    {accessiblePages.includes('DEVELOPER_SETTINGS') && (
      <Route>
        <Route
          path='devSettings'
          lazy={lazyDevSettings}
          handle={{ breadcrumb: 'navMenu:menu.devSettings' }}
        />
        <Route
          path='bulkUpsertRequests'
          handle={{ breadcrumb: 'navMenu:menu.bulkUpsertRequests' }}
        >
          <Route index lazy={lazyBulkUpsertRequests} />
          <Route
            path=':bulkUpsertRequestId'
            lazy={lazyBulkUpsertItems}
            handle={{ breadcrumb: BulkUpsertItemsBreadcrumb }}
          />
        </Route>
      </Route>
    )}
    {accessiblePages.includes('REPORTS') && (
      <Route path='reports' handle={{ breadcrumb: 'navMenu:menu.reports' }}>
        <Route index lazy={lazyReports} />
        <Route
          path='new'
          lazy={lazyNewReport}
          handle={{ breadcrumb: 'customReports:newReport' }}
        />
        <Route
          path=':id'
          lazy={lazyViewReport}
          handle={{ breadcrumb: CustomReportBreadcrumb }}
        />
      </Route>
    )}
    {accessiblePages.includes('WORKFLOWS') && (
      <Route
        path='workflows'
        lazy={lazyWorkflows}
        handle={{ breadcrumb: 'navMenu:menu.workflows' }}
      />
    )}

    <Route path='refinitivShowcase' lazy={lazyRefinitivShowcase} />

    <Route path='settings'>
      <Route path='general' lazy={lazyGeneralSettings} />
      <Route path='designer' lazy={lazyDesignerSettings} />
      <Route
        path='textDesigner'
        element={
          <FlagsmithRouteGuard
            flag={FlagsmithFlags.text_variants}
            redirectTo='/settings'
          >
            <TextDesignerSettings />
          </FlagsmithRouteGuard>
        }
      />
      <Route
        path='authenticationTheme'
        lazy={lazyAuthenticationDesignerSettings}
      />
      <Route path='errorTheme' lazy={lazyErrorPageDesignerSettings} />
      <Route path='accessUsers' lazy={lazyAccessUsersSettings} />
      <Route path='authenticationSettings' lazy={lazyAuthenticationSettings} />
      <Route path='accessSettings' lazy={lazyAccessSettings} />
      <Route
        path='productExperience'
        lazy={lazyGeneralProductExperienceSettings}
      />
      <Route path='dashboards'>
        <Route index lazy={lazyDashboards} />
        <Route
          path='editDashboard/:dashboardId'
          handle={{ breadcrumb: DashboardBreadcrumb }}
          lazy={lazyEditDashboard}
        />
      </Route>
      <Route path='localizations' lazy={lazyLocalizationSettings} />
      <Route
        path='localizationConfiguration'
        lazy={lazyLocalizationConfigurationSettings}
      />
      <Route path='notifications' lazy={lazyNotifications} />
      <Route
        path='alertBanners'
        handle={{ breadcrumb: 'navMenu:menu.alertBanners' }}
      >
        <Route
          index
          element={
            <FlagsmithRouteGuard
              flag={FlagsmithFlags.alert_banners}
              redirectTo='/settings'
            >
              <AlertBanners />
            </FlagsmithRouteGuard>
          }
        />
        <Route
          path='new'
          lazy={lazyNewAlertBanner}
          handle={{ breadcrumb: 'alertBanners:newBanner' }}
        />
        <Route
          path=':id'
          lazy={lazyEditAlertBanner}
          handle={{ breadcrumb: 'alertBanners:editBanner' }}
        />
      </Route>
      <Route path='forms' lazy={lazyForms} />
      <Route path='clientReports'>
        <Route index lazy={lazyClientReports} />
        <Route
          path=':id'
          handle={{ breadcrumb: ClientReportTemplateBreadcrumb }}
          lazy={lazyClientReportTemplate}
        />
      </Route>
      <Route
        path='statementsAndTaxDocuments'
        lazy={lazyStatementsAndTaxDocumentsSettings}
      />
      <Route path='customFields' lazy={lazyCustomFieldsSettings} />
      <Route path='repCodes' lazy={lazyRepCodesSettings} />
      <Route
        path='customAttributeLabels'
        lazy={lazyCustomAttributeLabelsSettings}
      />
      <Route path='pageConfigurations'>
        <Route index lazy={lazyPageConfigurationsSettings} />
        <Route
          path=':id'
          handle={{ breadcrumb: 'navMenu:menu.pageConfiguration' }}
          lazy={lazyPageConfiguration}
        />
      </Route>
      <Route path='homePageConfigurations'>
        <Route index lazy={lazyHomePageConfigurationsSettings} />
        <Route
          path=':homePageConfigurationId'
          handle={{ breadcrumb: 'navMenu:menu.homePageConfiguration' }}
          lazy={lazyHomePageConfigurationEditor}
        />
      </Route>
      <Route path='staticPageWidgetConfigurations'>
        <Route index lazy={lazyStaticPageWidgetConfigurationsSettings} />
        <Route
          path=':id'
          handle={{ breadcrumb: 'navMenu:menu.staticPageWidgetConfiguration' }}
          lazy={lazyStaticPageWidgetConfiguration}
        />
      </Route>
      <Route path='configurableTablePages'>
        <Route index lazy={lazyConfigurableTablePagesSettings} />
        <Route
          path=':configurableTablePageId'
          handle={{ breadcrumb: 'navMenu:menu.configurableTablePage' }}
          lazy={lazyConfigurableTablePageEditor}
        />
      </Route>
      <Route path='workflows'>
        <Route index lazy={lazyWorkflowsSettings} />
        <Route
          path=':id'
          handle={{ breadcrumb: 'navMenu:menu.configureWorkflow' }}
          lazy={lazyConfigureWorkflowPage}
        />
      </Route>
      <Route path='newsAndInsights' lazy={lazyNewsAndInsights} />
      <Route path='downtimeSchedule' lazy={lazyDowntimeSchedulesSettings} />
      <Route path='transfers' lazy={lazyTransfersSettings} />
      <Route path='trades' lazy={lazyTradesSettings} />
      <Route path='tradingSchedules' lazy={lazyTradingSchedulesSettings} />
      <Route path='assetClasses' lazy={lazyAssetClasses} />
      <Route path='compliance' lazy={lazyComplianceSettings} />
      <Route
        path='flagPolicies'
        element={
          <FlagsmithRouteGuard
            flag={FlagsmithFlags.recon_flags_dashboard}
            redirectTo='/settings'
          >
            <FlagPoliciesSettings />
          </FlagsmithRouteGuard>
        }
      />
      <Route
        path='portfolioManagementSettings'
        lazy={lazyPortfolioManagementSettings}
      />
      <Route path='feeGrids' lazy={lazyFeeGridsSettings} />
      <Route path='billingSchedules' lazy={lazyBillingSchedulesSettings} />
      <Route path='feeSettings' lazy={lazyFeeSettings} />
      <Route path='apiTokens' lazy={lazyApiTokensSettings} />
      <Route path='webhooks' lazy={lazyWebhooksSettings} />
      <Route path='embedded' lazy={lazyEmbeddedSettings} />
      <Route path='roleSettings' lazy={lazyRoleSettings} />
      <Route path='roleProfiles' lazy={lazyRoleProfilesSettings} />
      <Route path='roles' lazy={lazyRolesSettings} />
      <Route path='accounts' lazy={lazyAccountsTypesSettings} />
      <Route path='accountSettings' lazy={lazyAccountSettings} />
      <Route path='custodianConnections'>
        <Route index lazy={lazyCustodianConnectionsSettings} />
        <Route
          path=':custodianConnectionId'
          lazy={lazyCustodianConnectionDetail}
          handle={{
            breadcrumb: 'settings:custodianConnections.detail.breadcrumb',
          }}
        />
      </Route>
      <Route path='taxRankings' lazy={lazyTaxRankingsSettings} />
      <Route path='formDefinitions' lazy={lazyFormDefinitionsSettings} />
      <Route path='formMapper' lazy={lazyFormMapper} />
      <Route path='featureFlags' lazy={lazyFeatureFlagsSettings} />
      <Route path='integrations'>
        <Route index lazy={lazyIntegrationSettings} />
        <Route
          path='bulkUpsertRequests'
          handle={{ breadcrumb: 'navMenu:menu.bulkUpsertRequests' }}
        >
          <Route index lazy={lazyBulkUpsertRequests} />
          <Route
            path=':bulkUpsertRequestId'
            lazy={lazyBulkUpsertItems}
            handle={{ breadcrumb: BulkUpsertItemsBreadcrumb }}
          />
        </Route>
      </Route>

      <Route>
        <Route
          path='bulkImport'
          handle={{ breadcrumb: 'navMenu:menu.bulkImport' }}
        >
          <Route index lazy={lazyBulkImportsSettings} />
          <Route
            path=':bulkImportId'
            handle={{ breadcrumb: BulkImportBreadcrumb }}
          >
            <Route index lazy={lazyBulkImportFilesSettings} />
            <Route
              path='file/:importFileId'
              lazy={lazyBulkImportFileLineItemsSettings}
              handle={{ breadcrumb: ImportFileBreadcrumb }}
            />
          </Route>
        </Route>
      </Route>
    </Route>

    <Route path='*' element={<FallbackRoute />} />
  </Route>
);

/* Access to the non-accessible or non-existent URI */
const FallbackRoute = () => {
  const { accessiblePages } = usePermissions();
  const { userContext, activeEntity } = useContext(UserContext);
  const { flagEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const pageIsAccessible = (key: string): boolean =>
    accessiblePages.includes(key);

  /* Redirects the browser to the first accessible page */
  const menuSections = useMenuSections();
  const allMenuItems = menuSections
    .filter((sect) => sect.subSections.length > 0)
    .flatMap((sect) => sect.subSections as SubSection[]);
  const accessibleMenuItems = allMenuItems.filter(
    (item) =>
      (item.accessKey && pageIsAccessible(item.accessKey)) ||
      !!item.configurableTablePage,
  );
  const firstAccessiblePath = accessibleMenuItems[0]?.path;

  useEffect(() => {
    if (
      firstAccessiblePath &&
      (!userContext.role?.homePageConfiguration ||
        !flagEnabled('CONFIGURABLE_HOME_PAGES'))
    ) {
      // For SIMPLE navigation entity users, entityMenu owns the redirect to
      // /clients/:id. If we navigate to firstAccessiblePath (/clients) here,
      // the Individuals table mounts for one frame before entityMenu corrects
      // it — causing the flash reported in OVT-5010.
      if (
        userContext.role?.navigationStyle === NavigationStyles.SIMPLE &&
        activeEntity?.id
      ) {
        return;
      }
      navigate(firstAccessiblePath);
    }
  }, [
    navigate,
    firstAccessiblePath,
    userContext.role,
    flagEnabled,
    activeEntity,
  ]);

  if (
    userContext.role?.homePageConfiguration &&
    flagEnabled('CONFIGURABLE_HOME_PAGES')
  ) {
    return <HomePage />;
  }

  return <></>;
};

export const AppRouter = () => {
  const { accessiblePages } = usePermissions();
  const { userContext, activeEntity } = useContext(UserContext);

  const households = filterHouseholdsWithAccess(
    activeEntity?.households || [],
    activeEntity?.id,
    userContext,
  );
  const isHouseholdDefaultView =
    !!userContext.role?.makeHouseholdDefaultView && households.length > 0;

  const accessiblePagesKey = accessiblePages.join(',');
  const router = useMemo(() => {
    const routesArray = createRoutesFromElements(
      accesibleRoutes(accessiblePages, isHouseholdDefaultView),
    );
    return createBrowserRouter(routesArray);
  }, [accessiblePagesKey, isHouseholdDefaultView]);

  // Dispose the replaced/unmounted router so stale routers don't leak or navigate.
  useEffect(() => () => router.dispose(), [router]);

  return <RouterProvider router={router} fallbackElement={<BootSpinner />} />;
};
